import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, FileText, Calendar, Wand2, Lock } from 'lucide-react';
import { SmartSearchBar } from '../Layout/SmartSearchBar';
import { useAuth } from '../../hooks/useAuth';
import { useListingTemplates } from '../../hooks/useListingTemplates';
import { usePrompts } from '../../hooks/usePrompts';
import { ListingTemplateCard } from './ListingTemplateCard';

export const ListingTemplatesPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { templates, isLoading } = useListingTemplates(user?.id || null);
  
  // We need the prompts hook for the sidebar filters, but we'll use empty filters
  const {
    filters,
    setFilters,
    getAllPlatforms,
    getAllDomains,
    getAllOutputs,
    getAllWebPlatforms,
  } = usePrompts(user?.id || null);

  const handleNewTemplate = () => {
    navigate('/listing-templates/new');
  };

  const handleSearchChange = () => {
    // No search functionality for listing templates yet
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-modular-canvas-fog via-modular-ivory-mist to-modular-lunar-sage 
      dark:bg-dark-gradient transition-all duration-500">
      
      {/* Floating Search Bar */}
      <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-40 w-full max-w-md lg:max-w-xl xl:max-w-2xl px-4">
        <div className="bg-white/95 dark:bg-dark-surface/95 backdrop-blur-xl border border-gray-200/60 dark:border-dark-border/40 rounded-lg shadow-sm">
          <div className="p-2">
            <SmartSearchBar
              searchQuery=""
              onSearchChange={handleSearchChange}
              prompts={[]}
              recentlyExecuted={[]}
            />
          </div>
        </div>
      </div>

      <main className="flex-1 p-6 pt-20">
        <div className="w-full">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-3">
                <Lock className="h-8 w-8 text-modular-frosted-indigo dark:text-modular-frosted-indigo" />
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 dark:text-dark-text">
                    Listing Templates
                  </h1>
                  <p className="text-gray-600 dark:text-dark-text-secondary">
                    Create reusable listing templates and generate variations with AI
                  </p>
                </div>
              </div>
            </div>

            <button
              onClick={handleNewTemplate}
              className="bg-modular-primary-gradient text-white px-6 py-3 rounded-lg hover:shadow-modular-glow 
                transition-all duration-300 shadow-fire-card transform hover:scale-105 
                active:scale-95 flex items-center font-semibold"
            >
              <Plus className="h-5 w-5 mr-2" />
              New Template
            </button>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white/95 dark:bg-dark-surface/95 backdrop-blur-sm rounded-xl 
              border border-modular-stone-alloy/30 dark:border-modular-graphite-smoke/20 p-6 hover:shadow-modular-card 
              dark:hover:shadow-dark-card transition-all duration-300">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                    Total Templates
                  </p>
                  <p className="text-2xl font-bold text-fire-600 dark:text-fire-400">
                    {templates.length}
                  </p>
                </div>
                <FileText className="h-8 w-8 text-fire-500 dark:text-fire-400" />
              </div>
            </div>

            <div className="bg-white/95 dark:bg-dark-surface/95 backdrop-blur-sm rounded-xl 
              border border-emerald-200/30 dark:border-emerald-600/20 p-6 hover:shadow-lg 
              transition-all duration-300">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                    Recent Templates
                  </p>
                  <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                    {templates.filter(t => {
                      const weekAgo = new Date();
                      weekAgo.setDate(weekAgo.getDate() - 7);
                      return t.createdAt >= weekAgo;
                    }).length}
                  </p>
                </div>
                <Calendar className="h-8 w-8 text-emerald-500 dark:text-emerald-400" />
              </div>
            </div>

            <div className="bg-white/95 dark:bg-dark-surface/95 backdrop-blur-sm rounded-xl 
              border border-purple-200/30 dark:border-purple-600/20 p-6 hover:shadow-lg 
              transition-all duration-300">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                    AI Variations
                  </p>
                  <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                    ∞
                  </p>
                </div>
                <Wand2 className="h-8 w-8 text-purple-500 dark:text-purple-400" />
              </div>
            </div>
          </div>

          {/* Content */}
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="text-center">
                <div className="relative mb-6">
                  <div className="animate-spin rounded-full h-12 w-12 border-4 border-modular-stone-alloy 
                    border-t-modular-frosted-indigo mx-auto"></div>
                  <Lock className="absolute inset-0 h-6 w-6 text-modular-frosted-indigo m-auto animate-pulse" />
                </div>
                <p className="text-gray-600 dark:text-dark-text-secondary font-medium">
                  Loading your templates...
                </p>
              </div>
            </div>
          ) : templates.length === 0 ? (
            <div className="text-center py-16">
              <div className="relative mb-6">
                <div className="w-24 h-24 mx-auto bg-modular-primary-gradient rounded-full flex items-center 
                  justify-center shadow-modular-glow animate-modular-pulse">
                  <Lock className="h-12 w-12 text-white" />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-dark-text mb-3">
                No listing templates yet
              </h3>
              <p className="text-gray-600 dark:text-dark-text-secondary mb-8 max-w-md mx-auto">
                Create your first master listing template to start generating AI-powered variations for your products.
              </p>
              
              <div className="flex items-center justify-center">
                <button
                  onClick={handleNewTemplate}
                  className="bg-modular-primary-gradient text-white px-6 py-3 rounded-lg hover:shadow-modular-glow 
                    transition-all duration-300 shadow-fire-card transform hover:scale-105 
                    active:scale-95 flex items-center"
                >
                  <Plus className="h-5 w-5 mr-2" />
                  Create First Template
                </button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {templates.map((template) => (
                <ListingTemplateCard
                  key={template.id}
                  template={template}
                  onClick={() => navigate(`/listing-templates/${template.id}`)}
                />
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};